import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Center,
  Group,
  Paper,
  Skeleton,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import dayjs from "dayjs";

import { BillingReportDateFilters } from "../../components/reports/BillingReportDateFilters";
import { AccessionLogPrefixFilter } from "../../components/reports/AccessionLogPrefixFilter";
import { useAccessionLogPrefixes } from "../../hooks/useAccessionLogPrefixes";
import type {
  AccessionLogPrefixOption,
  AccessionLogPrefixSelection,
} from "../../types/accessionLogPrefix";
import {
  DateRange,
  getLastMonthRange,
} from "../../components/reports/ReportDateRangePicker";
import { ReportFileExportButton } from "../../components/reports/ReportFileExportButton";
import { useAccessionLogReport } from "../../hooks/useReports";
import { useLabTodayDateOnly } from "../../hooks/useLabTodayDateOnly";
import { formatDateOnlyForDisplay, parseDateOnly } from "../../utils/dateOnly";
import { formatLabDateTime } from "../../utils/labTime";
import {
  AccessionLogDateBasis,
  reportsService,
} from "../../services/reports.service";

const DEFAULT_RANGE = getLastMonthRange();
const EMPTY_PREFIXES: AccessionLogPrefixOption[] = [];

export function AccessionLogReportContent() {
  const [prefixSelection, setPrefixSelection] =
    useState<AccessionLogPrefixSelection>({ prefixMode: "all" });
  const {
    data: prefixes = EMPTY_PREFIXES,
    isLoading: prefixesLoading,
    isError: prefixesError,
    refetch: refetchPrefixes,
  } = useAccessionLogPrefixes();
  const labToday = useLabTodayDateOnly();
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE);
  const [dateBasis, setDateBasis] =
    useState<AccessionLogDateBasis>("first_reported");
  const params = useMemo(
    () => ({
      startDate: dayjs(dateRange.startDate).format("YYYY-MM-DD"),
      endDate: dayjs(dateRange.endDate).format("YYYY-MM-DD"),
      dateBasis,
      ...prefixSelection,
    }),
    [dateBasis, dateRange, prefixSelection]
  );
  const { data, isLoading, isFetching, isError } =
    useAccessionLogReport(params);
  const reportIsLoading = useMemo(
    () => isLoading || isFetching,
    [isFetching, isLoading]
  );
  const exportParams = useMemo(
    () => ({ ...params, reportRunId: data?.reportRunId }),
    [data?.reportRunId, params]
  );

  const handleDateBasisChange = useCallback((value: string | null) => {
    if (value) setDateBasis(value as AccessionLogDateBasis);
  }, []);
  const handlePrefixChange = useCallback(
    (value: AccessionLogPrefixSelection) => setPrefixSelection(value),
    []
  );
  const handlePrefixRetry = useCallback(() => {
    void refetchPrefixes();
  }, [refetchPrefixes]);
  const handleDateRangeChange = useCallback((value: DateRange) => {
    setDateRange(value);
  }, []);
  const handleExcelExport = useCallback(
    () => reportsService.exportAccessionLogExcel(exportParams),
    [exportParams]
  );
  const handlePdfExport = useCallback(
    () => reportsService.exportAccessionLogPdf(exportParams),
    [exportParams]
  );

  return (
    <Stack gap="lg">
      <Paper p="md" withBorder>
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Group align="flex-end">
            <BillingReportDateFilters
              dateBasis={dateBasis}
              dateRange={dateRange}
              includeDateAccessioned
              onDateBasisChange={handleDateBasisChange}
              onDateRangeChange={handleDateRangeChange}
            />
            <AccessionLogPrefixFilter
              options={prefixes}
              value={prefixSelection}
              onChange={handlePrefixChange}
              loading={prefixesLoading}
              disabled={prefixesError || prefixesLoading}
            />
          </Group>
          <Group gap="xs">
            <ReportFileExportButton
              label="Export Excel"
              filename={`accession-log-${labToday}.xlsx`}
              fetchFile={handleExcelExport}
              disabled={
                !data ||
                reportIsLoading ||
                isError ||
                prefixSelection.prefixMode === "none"
              }
            />
            <ReportFileExportButton
              label="Export PDF"
              filename={`accession-log-${labToday}.pdf`}
              fetchFile={handlePdfExport}
              disabled={
                !data ||
                reportIsLoading ||
                isError ||
                prefixSelection.prefixMode === "none"
              }
            />
          </Group>
        </Group>
      </Paper>

      {prefixesError && (
        <Alert color="red" title="Prefixes could not be loaded" role="alert">
          The prefix list is unavailable. Retry to select prefixes.
          <Button variant="subtle" size="xs" onClick={handlePrefixRetry}>
            Retry prefixes
          </Button>
        </Alert>
      )}

      {isError && (
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertCircle size={16} />}
          title="Accession log could not be generated"
          role="alert"
        >
          No cases are shown or exportable because the report could not be
          verified. Retry before using it for billing reconciliation.
        </Alert>
      )}

      {data && !isError && !reportIsLoading && (
        <Group grow>
          <Paper p="md" withBorder>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
              Total Cases
            </Text>
            <Text size="xl" fw={700}>
              {data.totalCases}
            </Text>
          </Paper>
          <Paper p="md" withBorder>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
              Total Parts
            </Text>
            <Text size="xl" fw={700}>
              {data.totalParts}
            </Text>
            <Text size="xs" c="dimmed">
              Generated {formatLabDateTime(data.generatedAt)}
            </Text>
          </Paper>
        </Group>
      )}

      {reportIsLoading ? (
        <Stack gap="xs">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} h={40} />
          ))}
        </Stack>
      ) : isError ? null : !data || data.rows.length === 0 ? (
        <Center py="xl">
          <Text c="dimmed">
            {prefixSelection.prefixMode === "none"
              ? "Select at least one prefix to view results."
              : "No qualifying cases for the selected filters."}
          </Text>
        </Center>
      ) : (
        <Table.ScrollContainer minWidth={1000}>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Accession #</Table.Th>
                <Table.Th>Patient</Table.Th>
                <Table.Th>Physician</Table.Th>
                <Table.Th>Collected Date</Table.Th>
                <Table.Th>Received Date</Table.Th>
                <Table.Th>Specimen</Table.Th>
                <Table.Th>Pathologist</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.rows.map((row) => (
                <Table.Tr key={row.caseId}>
                  <Table.Td>{row.accessionNumber}</Table.Td>
                  <Table.Td>{row.patientName}</Table.Td>
                  <Table.Td>{row.physicianName}</Table.Td>
                  <Table.Td>{displayDate(row.collectedDate)}</Table.Td>
                  <Table.Td>{displayDate(row.receivedDate)}</Table.Td>
                  <Table.Td>
                    <Stack gap={0}>
                      {row.specimens.map((specimen) => (
                        <Text key={specimen} size="sm">
                          {specimen}
                        </Text>
                      ))}
                    </Stack>
                  </Table.Td>
                  <Table.Td>{row.pathologistName}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}

function displayDate(value: string): string {
  const dateOnly = parseDateOnly(value);
  return dateOnly ? formatDateOnlyForDisplay(dateOnly) : "Not recorded";
}
